import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import blueluxLogo from "@/assets/bluelux-logo.png";

interface NavigationProps {
  isHomePage?: boolean;
}

export const Navigation = ({ isHomePage = false }: NavigationProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Handle scrolling to hash sections when component mounts
  useEffect(() => {
    if (isHomePage && window.location.hash) {
      const sectionId = window.location.hash.substring(1);
      // Use requestAnimationFrame to avoid forced reflows
      requestAnimationFrame(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  }, [isHomePage]);

  const scrollToSection = (sectionId: string) => {
    if (isHomePage) {
      // Use requestAnimationFrame to avoid forced reflows
      requestAnimationFrame(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      });
    } else {
      navigate(`/#${sectionId}`);
    }
    setIsMenuOpen(false);
  };

  const handleContactClick = () => {
    if (isHomePage) {
      scrollToSection("contact");
    } else {
      navigate("/#contact");
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Link to="/" className="flex items-center">
              <img 
                src={blueluxLogo} 
                alt="BlueLux Pool Service" 
                className="h-8 md:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            <button
              onClick={() => scrollToSection("services")}
              className="text-foreground hover:text-primary transition-colors"
            >
              Services
            </button>
            <Link
              to="/pricing"
              className="text-foreground hover:text-primary transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/jobs"
              className="text-foreground hover:text-primary transition-colors"
            >
              Join Our Team
            </Link>
            <Link
              to="/sell-your-route"
              className="text-foreground hover:text-primary transition-colors"
            >
              Sell Your Route
            </Link>
            <Link
              to="/marketplace"
              className="text-foreground hover:text-primary transition-colors"
            >
              Partner Marketplace
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              asChild
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6 py-2"
            >
              <a 
                href="https://app.bluelux.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Customer Login
              </a>
            </Button>
            <Button
              variant="outline"
              asChild
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-6 py-2"
            >
              <a href="https://team.bluelux.com">
                Team Login
              </a>
            </Button>
            <Button
              onClick={handleContactClick}
              className="bg-gold hover:bg-gold/90 text-gold-foreground font-semibold px-6 py-2"
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 touch-manipulation"
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 py-4 border-t animate-fade-in">
            <nav className="flex flex-col space-y-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <button
                onClick={() => scrollToSection("services")}
                className="text-left text-foreground hover:text-primary transition-colors py-2 px-2 touch-manipulation"
              >
                Services
              </button>
              <Link
                to="/pricing"
                className="text-left text-foreground hover:text-primary transition-colors py-2 px-2 touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link
                to="/jobs"
                className="text-left text-foreground hover:text-primary transition-colors py-2 px-2 touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Join Our Team
              </Link>
              <Link
                to="/sell-your-route"
                className="text-left text-foreground hover:text-primary transition-colors py-2 px-2 touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Sell Your Route
              </Link>
              <Link
                to="/marketplace"
                className="text-left text-foreground hover:text-primary transition-colors py-2 px-2 touch-manipulation"
                onClick={() => setIsMenuOpen(false)}
              >
                Partner Marketplace
              </Link>
              <div className="flex flex-col gap-3 mt-4">
                <Button
                  variant="outline"
                  asChild
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold w-full touch-manipulation"
                >
                  <a 
                    href="https://app.bluelux.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Customer Login
                  </a>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold w-full touch-manipulation"
                >
                  <a 
                    href="https://team.bluelux.com"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Team Login
                  </a>
                </Button>
                <Button
                  onClick={handleContactClick}
                  className="bg-gold hover:bg-gold/90 text-gold-foreground font-semibold w-full touch-manipulation"
                >
                  Book Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
    
    {/* Mobile Menu Backdrop */}
    {isMenuOpen && (
      <div 
        className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
        onClick={() => setIsMenuOpen(false)}
        aria-hidden="true"
      />
    )}
    </>
  );
};